import { Controller } from "@hotwired/stimulus"
import { animate } from "motion"

// Animation duration constants (in seconds)
const STANDARD_DURATION_LARGE_CHANGE = 0.8 // For large cost changes (> 7)
const STANDARD_DURATION_SMALL_CHANGE = 0.4 // For small cost changes (≤ 7)
const FAST_DURATION_LARGE_CHANGE = 0.4 // For large cost changes (> 7) with fast animation
const FAST_DURATION_SMALL_CHANGE = 0.2 // For small cost changes (≤ 7) with fast animation

export default class extends Controller {
  static values = {
    cost: Number,
    useAnimation: Boolean,
    fastAnimation: Boolean,
  }

  static targets = ["cost", "decimalPart"]

  // ===== LIFECYCLE METHODS =====

  connect() {
    this._initializeState()
    this._setupInitialVisibility()
  }

  disconnect() {
    this._cleanup()
  }

  // ===== PUBLIC API =====

  updateCost(newCost) {
    this.costValue = newCost
  }

  show() {
    if (!this.useAnimationValue || this.element.classList.contains("is-visible")) return

    this._clearTimers()
    this.element.classList.remove("hidden")

    this._showTimer = setTimeout(() => {
      this.element.classList.add("is-visible")
      this._showTimer = null
    }, 0)
  }

  hide() {
    if (!this.useAnimationValue || this.element.classList.contains("hidden")) return

    this._clearTimers()
    this.element.classList.remove("is-visible")
  }

  // ===== VALUE CHANGE HANDLERS =====

  costValueChanged(newValue, oldValue) {
    this._updateCostDisplay(newValue)

    if (this.useAnimationValue) {
      this._handleAnimatedVisibility(newValue, oldValue)
    } else {
      this._updateVisibility(newValue)
    }
  }

  // ===== PRIVATE METHODS =====

  _initializeState() {
    this._showTimer = null
    this._hideTimer = null
    this._costAnimation = null
    this._decimalUpdateTimer = null
  }

  _setupInitialVisibility() {
    if (this.useAnimationValue) {
      if (this.costValue > 0) {
        this._forceShow()
      } else {
        this._forceHide()
      }
    } else {
      this._updateVisibility(this.costValue)
    }
  }

  _cleanup() {
    this._clearTimers()
    this._cancelAnimation(this._costAnimation)
    if (this._decimalUpdateTimer) {
      clearTimeout(this._decimalUpdateTimer)
      this._decimalUpdateTimer = null
    }
  }

  _handleAnimatedVisibility(newValue, oldValue) {
    // Update visibility
    if (newValue === 0) {
      this.hide()
    } else {
      this.show()
    }

    // Animate number change
    this._animateCostChange(oldValue || 0, newValue)
  }

  _animateCostChange(from, to) {
    // Cancel any existing animation first
    this._cancelAnimation(this._costAnimation)

    // Clear any pending decimal update timer
    if (this._decimalUpdateTimer) {
      clearTimeout(this._decimalUpdateTimer)
      this._decimalUpdateTimer = null
    }

    const isHalfValue = to % 1 === 0.5
    const delay = isHalfValue ? this._getAnimationDuration(from, to) * 1000 - 200 : 100

    // Update decimal visibility with delay
    this._decimalUpdateTimer = setTimeout(() => {
      this._updateDecimalVisibility(to)
      this._decimalUpdateTimer = null
    }, delay)

    this._costAnimation = animate(from, to, {
      duration: this._getAnimationDuration(from, to),
      ease: "circOut",
      onUpdate: (latest) => {
        if (this.hasCostTarget) {
          this.costTarget.textContent = Math.floor(latest).toString()
        }
      },
      onComplete: () => {
        // Clean up reference when animation completes
        this._costAnimation = null
      },
    })
  }

  _updateCostDisplay(cost) {
    if (this.hasCostTarget) {
      if (this.useAnimationValue) {
        this.costTarget.textContent = Math.floor(cost).toString()
      } else {
        this.costTarget.textContent = cost.toString()
      }
    }
  }

  _updateDecimalVisibility(value) {
    if (this.hasDecimalPartTarget) {
      const isHalfValue = value % 1 === 0.5
      this.decimalPartTarget.classList.toggle("is-visible", isHalfValue)
    }
  }

  _getAnimationDuration(from, to) {
    if (this.fastAnimationValue) {
      return Math.abs(to - from) <= 7 ? FAST_DURATION_SMALL_CHANGE : FAST_DURATION_LARGE_CHANGE
    }
    return Math.abs(to - from) <= 7 ? STANDARD_DURATION_SMALL_CHANGE : STANDARD_DURATION_LARGE_CHANGE
  }

  _updateVisibility(cost) {
    const suffixWrapper = this.element.closest(".h-button__suffix")
    if (suffixWrapper) {
      suffixWrapper.style.display = cost === 0 ? "none" : "block"
    } else {
      this.element.style.display = cost === 0 ? "none" : "inline-flex"
    }
  }

  _forceShow() {
    this._clearTimers()
    this.element.classList.remove("hidden")
    this.element.classList.add("is-visible")
  }

  _forceHide() {
    this._clearTimers()
    this.element.classList.remove("is-visible")
    this.element.classList.add("hidden")
  }

  _clearTimers() {
    if (this._showTimer) {
      clearTimeout(this._showTimer)
      this._showTimer = null
    }
    if (this._hideTimer) {
      clearTimeout(this._hideTimer)
      this._hideTimer = null
    }
  }

  _cancelAnimation(animation) {
    try {
      animation?.cancel?.()
    } catch {
      // ignore errors
    }
    try {
      animation?.stop?.()
    } catch {
      // ignore errors
    }
  }
}
